/**
 * Pure decision function for the F5 / page-load restore path.
 *
 * Split out of main.ts so tests can exercise every branch without
 * pulling in main.ts's module-level DOM side effects (service worker
 * registration, wake-lock's `document.addEventListener`, etc.).
 *
 * Session 69 — FINISHED is now terminal; never auto-restore to results.
 * User-reported 2026-04-22: «вышел с autokvest.ru, через пару часов
 * вернулся — меня редиректнуло на /app/#/results». A persisted
 * completedAt from a finished game shouldn't pull the user back to a
 * stale results screen. Two sibling changes make this safe:
 *   (a) `main.ts` boot guard clears the persisted blob entirely when
 *       the stored snapshot is FINISHED.
 *   (b) `results-view.ts` «Новая игра» calls `clearAllGameData()`
 *       before navigating to /join.
 * Losing auto-restore to /results after an immediate F5 on results
 * is an intentional trade: deterministic cross-device behavior over
 * a 1% crash-recovery quality-of-life.
 *
 * Approach D (2026-05-08, resumeGame limbo) — also returns 'results' when
 * status is ACTIVE but currentLocationIndex >= locations.length. Defense
 * in depth for the post-resumeGame stale-snapshot edge case where a
 * naturally-finished team's localStorage holds an idx past the loaded
 * locations array. Pairs with the backend partition fix in
 * GameOrgControlOps.kt.
 */

import type { GameState } from '../store';
import type { Route } from '../router';

export function decideRestoreRoute(
  s: Pick<GameState, 'gameCode' | 'playerId' | 'completedAt' | 'status' | 'currentLocationIndex' | 'locations'>
): Route | null {
  if (!s.gameCode || !s.playerId) return null;
  // Session 69: FINISHED state never restores. Callers (main.ts boot
  // guard) should also clear the snapshot so the next load sees a
  // fresh slate. Left here as a defense-in-depth — even if a future
  // caller forgets to clear, we refuse to navigate into /results.
  if (s.status === 'FINISHED') return null;
  // Approach D — defense-in-depth for resumeGame post-fix edge case.
  // If the SERVER says ACTIVE but our local index is past the loaded
  // locations array, render results (don't crash on locations[idx]).
  // Guard on `locations.length > 0` so a freshly-joined player whose
  // locations haven't loaded yet (idx=0, length=0) still routes to
  // riddle / lobby normally.
  if (s.status === 'ACTIVE'
      && s.locations.length > 0
      && s.currentLocationIndex >= s.locations.length) {
    return 'results';
  }
  if (s.currentLocationIndex >= 0 && s.status === 'ACTIVE') return 'riddle';
  return null;
}
